import React,{Children,isValidElement,forwardRef} from 'react';
import {useAuth} from '../../Maindashboard/Context/AuthContext';
import policy from './actionPermissionPolicy.json';
export function actionLabel(children){return Children.toArray(children).map(n=>typeof n==='string'||typeof n==='number'?String(n):isValidElement(n)?(n.type?.displayName||n.type?.name||'')+' '+actionLabel(n.props.children):'').join(' ');}
export function inferAction(text){
 if(/\b(template|sample format|download format)\b/i.test(text))return 'template';
 if(/\b(export|download)\b/i.test(text))return 'export';
 if(/\bprint\b/i.test(text))return 'print';
 if(/\bview\b/i.test(text)&&!/\bedit\b|\bdelete\b/i.test(text))return 'view';
 if(/\b(cancel|close|back|previous|next|search|filter|refresh)\b/i.test(text)&&!/\bedit\b|\bdelete\b/i.test(text))return null;
 if(/\b(delete|trash2?|remove access)\b/i.test(text))return 'delete';
 if(/\b(edit|pencil|pencilline|update|adjust|activate|deactivate|toggle|toggleleft|toggleright|start|complete|handover)\b/i.test(text))return 'edit';
 if(/\b(import|upload|validate file)\b/i.test(text))return 'import';
 if(/\b(approve|finalize|send po|confirm sending)\b/i.test(text))return 'approve';
 if(/\b(pay|paid|payment)\b/i.test(text))return 'pay';
 if(/\b(receive|received)\b/i.test(text))return 'receive';
 if(/\b(inward)\b/i.test(text))return 'inward';
 if(/\b(outward)\b/i.test(text))return 'outward';
 if(/\b(transfer)\b/i.test(text))return 'transfer';
 if(/\b(add|create|generate|process|calculate|new|raise|mark attendance|bulk attendance)\b/i.test(text))return 'create';
 if(/\b(save|submit|confirm)\b/i.test(text))return 'write';
 return null;
}
export function canAction(auth,module,action){
 if(!action)return true;
 if(auth?.user?.role==='super_admin')return true;
 if(!auth?.user)return false;
 // Existing company configuration restrictions are not lifted by new keys.
 if(['roles','branches','counters'].includes(module)&&action!=='view')return false;
 const keys=auth.permissions||[],entry=policy[module];
 if(!entry)return false;
 if((entry.full||[]).some(k=>keys.includes(k)))return true;
 if(action==='view')return keys.includes(`${module}.view`)||(entry.read||[]).some(k=>keys.includes(k));
 if(action==='template')return canAction(auth,module,'import');
 if(module==='production'&&['issue','output','reconcile','learn_recipe'].includes(action)&&keys.includes('production.edit'))return true;
 if(action==='form')return ['create','edit','import','inward','outward','transfer','approve','pay'].some(a=>canAction(auth,module,a));
 if(action==='write')return ['create','edit'].some(a=>keys.includes(`${module}.${a}`));
 if(module==='menu'&&action==='import'&&keys.includes('menu.create'))return true;
 if(module==='sales_orders'&&action==='pay'&&['payments.manage','sales_payments.pay'].some(k=>keys.includes(k)))return true;
 if(module==='payroll'&&action==='pay'&&keys.includes('payslips.pay'))return true;
 if(module==='purchase_orders'&&action==='receive'&&keys.includes('inventory.movement'))return true;
 return keys.includes(`${module}.${action}`);
}
export function pageModule(path=''){
 if(typeof path==='string' && path.includes('/reports/department-profit'))return 'department_profit';
 if(typeof path!=='string')return null;
 if(/\/billing\/payments|\/gst\/payments|\/payments$/.test(path))return 'sales_payments';
 const rules=[[/access\/permissions/,'staff'],[/access\/roles/,'roles'],[/vendor.?types/,'vendor_types'],[/vendors\/commissions/,'vendor_payments'],[/vendors/,'vendors'],[/customers/,'customers'],[/sales.?orders/,'sales_orders'],[/menu-master/,'menu'],[/inventory\/material-issues/,'material_issues'],[/inventory/,'inventory'],[/recipes/,'recipes'],[/production\/kitchen/,'kitchen'],[/production/,'production'],[/attendance/,'attendance'],[/payslips/,'payslips'],[/payroll/,'payroll'],[/po\/|\/poc/,'purchase_orders'],[/counters/,'counters'],[/departments/,'departments'],[/branches/,'branches'],[/chart.?of.?accounts|\/coa/,'accounts'],[/journal/,'journal'],[/expenses/,'expenses'],[/income/,'income']];
 return rules.find(([pattern])=>pattern.test(path))?.[1]||null;
}
export function canPage(auth,permission,path=''){
 if(!auth?.user)return false;
 if(permission==='help.view'&&path==='/admindashboard/help')return true;
 if(auth.user.role==='super_admin')return true;
 if(permission==='pos.settings.manage')return false;
 if(!permission)return path==='/admindashboard'?(auth.permissions||[]).includes('dashboard'):false;
 const keys=auth?.permissions||[],module=pageModule(path);
 if(module==='purchase_orders'&&path.endsWith('/poc'))return canAction(auth,module,'create');
 if(module&&/\/edit(?:\/|$)/.test(path))return canAction(auth,module,'edit');
 if(module&&/\/add(?:\/|$)/.test(path))return canAction(auth,module,'create');
 // A module's generic view key must not open sibling financial/approval pages.
 const primaryViews={'orders.view':'sales_orders.view','production.recipes':'recipes.view',
  'branches.demands':'demands.view',
  'production.batches':'production.view','salary.manage':'payroll.view',
  'payments.manage':'sales_payments.view','accounting.coa':'accounts.view',
  'accounting.journal':'journal.view','accounting.expenses':'expenses.view',
  'accounting.income':'income.view'};
 if(permission==='staff.manage'&&/access\/permissions/.test(path))return keys.includes('staff.manage')||keys.includes('staff.view');
 if(/access\/roles/.test(path))return keys.includes(permission)||keys.includes('roles.view');
 return keys.includes(permission)||Boolean(primaryViews[permission]&&keys.includes(primaryViews[permission]));
}
export function firstAccessiblePage(auth,items){
 return items.find(item=>item.permission!=='help.view'&&(!item.superAdminOnly||auth?.user?.role==='super_admin')&&canPage(auth,item.permission,item.path))?.path||
  items.find(item=>item.permission==='help.view'&&canPage(auth,item.permission,item.path))?.path||'/unauthorized';
}
const PermissionButton=forwardRef(function PermissionButton({permissionModule,permissionAction,children,...props},ref){
 const auth=useAuth();const action=permissionAction||inferAction(actionLabel(children)+' '+(props['aria-label']||'')+' '+(props.title||''));
 if(!canAction(auth,permissionModule,action))return null;
 return <button {...props} ref={ref}>{children}</button>;
});
PermissionButton.permissionAware=true;
export default PermissionButton;
